import { createAccessControl } from "better-auth/plugins/access";
// adminAc: built-in admin role statements (user, session management)
import { adminAc } from "better-auth/plugins/admin/access";

// ── Permission Statements ─────────────────────────────────────────────────────
// All resources and their possible actions — defined per SRS §7.2.
// These are compile-time constants; changing them requires a redeploy.
export const statement = {
  ...adminAc.statements, // user, session (built-in admin operations)

  // ── Product — Org User Resources ─────────────────────────────────────────
  analytic: ["read"],
  import: ["create", "read"],
  scheme: ["create", "read", "update", "complete", "cancel"],
  goal: ["create", "read", "update", "delete"],
  team_member: ["create", "read", "update", "delete"],
  target: ["create", "read", "update", "delete"],
  task: ["create", "read", "complete"],
  note: ["create", "read", "delete"],
  ask_query: ["create", "read"],
  channel_enrollment: ["read"],

  // ── Shared Resources (accessed by both roles) ─────────────────────────────
  territory: ["create", "read", "update", "soft-delete"],
  whatsapp_template: ["create", "read", "update", "submit-for-approval"],

  // ── Super Admin Panel Resources ───────────────────────────────────────────
  organisation: ["create", "read", "update", "deactivate"],
} as const;

export const ac = createAccessControl(statement);

// ── Role Definitions ──────────────────────────────────────────────────────────
// Defined per SRS §7.3.

/**
 * super_admin — Internal platform operators (SRS §7.3).
 * Grants access to provisioning panel, user management, org management,
 * territory configuration, and WhatsApp template management.
 * Does NOT grant access to any product screens.
 */
export const superAdminRole = ac.newRole({
  // All built-in admin capabilities (user: create/list/set-role/ban/impersonate/delete/set-password;
  // session: list/revoke/delete)
  ...adminAc.statements,
  // Super Admin Panel resources
  organisation: ["create", "read", "update", "deactivate"],
  territory: ["create", "read", "update", "soft-delete"],
  whatsapp_template: ["create", "read", "update", "submit-for-approval"],
});

/**
 * admin — Every user within a customer organisation (SRS §7.3).
 * Grants full access to all product screens and data within the user's org.
 * Does NOT grant access to better-auth admin endpoints (user/session management).
 */
export const adminRole = ac.newRole({
  // Analytics & exploration (all main product screens)
  analytic: ["read"],
  // Data onboarding
  import: ["create", "read"],
  // Scheme & action management
  scheme: ["create", "read", "update", "complete", "cancel"],
  // Goal & team management
  goal: ["create", "read", "update", "delete"],
  team_member: ["create", "read", "update", "delete"],
  target: ["create", "read", "update", "delete"],
  // Lightweight collaboration
  task: ["create", "read", "complete"],
  note: ["create", "read", "delete"],
  // Ask engine
  ask_query: ["create", "read"],
  // Channel health (read-only for org users)
  channel_enrollment: ["read"],
  // Territory: read-only for org users (territory picker across all screens)
  territory: ["read"],
  // WhatsApp templates: read-only for org users (approved templates in ActionCreator)
  whatsapp_template: ["read"],
});

// ── Exported roles map (passed to admin() plugin) ─────────────────────────────
export const roles = {
  super_admin: superAdminRole,
  admin: adminRole,
};

// ── Multi-role permission resolution ─────────────────────────────────────────
// Better Auth stores multiple roles as comma-separated strings (e.g. "admin,billing").
// This utility merges all role permissions and returns the union set.
export function resolveEffectivePermissions(
  userRoles: string[],
  resource: string
): string[] {
  const allActions = new Set<string>();
  for (const roleName of userRoles) {
    const role = roles[roleName as keyof typeof roles];
    if (role && resource in role.statements) {
      const actions =
        role.statements[resource as keyof typeof role.statements];
      if (Array.isArray(actions)) {
        actions.forEach((a) => allActions.add(a as string));
      }
    }
  }
  return Array.from(allActions);
}
